import React, { useEffect, useState } from 'react';
import { useUser, useAuth } from '@clerk/clerk-react';
import { Navigate, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

interface ConversationSession {
  id: string;
  session_type: 'eulogy' | 'tribute';
  deceased_name: string | null;
  status: 'in_progress' | 'completed' | 'abandoned';
  completion_percentage: number;
  created_at: string;
  updated_at: string;
  testament_license_id?: string | null;
  // Phase 4: ISO timestamp if the 90-day writing window has closed; null otherwise.
  archived_at?: string | null;
  // Phase 5 (forward-compat): set when Memory Collection PDF is finalised.
  finalised_at?: string | null;
  // Phase 6: tier + ownership so the Dashboard can flag "shared with you" cards
  // distinctly from owned projects and show the $40 Family & Friends upgrade
  // CTA only on owner cards. `is_owner=false` means the caller is an accepted
  // writer on this license (single_tribute → read-only; family_memorial →
  // writer). `role` is a convenience duplicate of the isOwner-derived value
  // served by /api/user/sessions.
  tier?: 'single_tribute' | 'family_memorial' | null;
  is_owner?: boolean;
  role?: 'owner' | 'writer';
  // 2026-07-12: how many family & friends have been invited to share memories
  // on this piece. Drives the "Waiting for the first shared memory" sub-line.
  collaborators_invited?: number;
}

interface GeneratedEulogy {
  id: string;
  session_id: string;
  style: string;
  word_count: number;
  created_at: string;
}

// 2026-07-05: a PAID license whose conversation hasn't been created yet.
// Payment is the moment the piece exists — these must show on the Dashboard
// as "ready to begin" (the onboarding details are saved on the license), even
// if the first visit to the conversation page failed or never happened.
interface UnstartedLicense {
  license_id: string;
  deceased_name: string | null;
  relationship: string | null;
  // Known for licenses created after migration 028; null for older ones —
  // those route through /new-project so the user picks eulogy vs tribute.
  piece_type: 'eulogy' | 'tribute' | null;
  tier: 'single_tribute' | 'family_memorial' | null;
  is_owner: boolean;
  created_at: string;
}

interface Contribution {
  id: string;
  session_id: string;
  contributor_name: string;
  contributor_relationship: string | null;
  content: {
    favourite_memory?: string;
    personality_traits?: string;
    additional_notes?: string;
  };
  is_approved: boolean;
  created_at: string;
}

const Dashboard: React.FC = () => {
  const { isLoaded, isSignedIn, user } = useUser();
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<ConversationSession[]>([]);
  const [eulogies, setEulogies] = useState<GeneratedEulogy[]>([]);
  const [contributions, setContributions] = useState<Contribution[]>([]);
  const [unstartedLicenses, setUnstartedLicenses] = useState<UnstartedLicense[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  // Phase 5: per-row download state for Memory Collection buttons on Completed cards
  const [mcDownloadingId, setMcDownloadingId] = useState<string | null>(null);
  const [mcError, setMcError] = useState<string | null>(null);
  // Phase 6 / 2026-07-12: set while the Family & Friends $40 checkout is being
  // created. The upgrade now starts from the family-memories page (via
  // /dashboard?upgrade=<sessionId>), so this drives a clear "Preparing your
  // upgrade…" banner during the brief hand-off to Stripe — silence at that
  // moment is exactly what unnerves a non-technical user.
  const [upgradeStartingId, setUpgradeStartingId] = useState<string | null>(null);
  const [upgradeError, setUpgradeError] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Cyrano — Your Dashboard';
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!isSignedIn) return;
      try {
        const token = await getToken();
        const resp = await fetch('/api/user/sessions', {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (resp.ok) {
          const data = await resp.json();
          setSessions(data.sessions || []);
          setEulogies(data.eulogies || []);
          setContributions(data.contributions || []);
          setUnstartedLicenses(data.unstartedLicenses || []);
        }
      } catch (err) {
        console.error('Error fetching user data:', err);
      } finally {
        setLoadingData(false);
      }
    };
    if (isLoaded && isSignedIn) fetchUserData();
  }, [isLoaded, isSignedIn, getToken]);

  useEffect(() => {
    if (!isLoaded || !isSignedIn || !loadingData) return;
    const t = setTimeout(() => setLoadingData(false), 8000);
    return () => clearTimeout(t);
  }, [isLoaded, isSignedIn, loadingData]);

  // 2026-07-12: the $40 Family & Friends upgrade moved off the card row into
  // the family-memories page (so it can never read as the price of inviting).
  // That static page can't call the authed checkout endpoint itself, so it
  // links back here with ?upgrade=<sessionId> and we start checkout once the
  // sessions have loaded.
  useEffect(() => {
    if (sessions.length === 0) return;
    const upgradeId = new URLSearchParams(window.location.search).get('upgrade');
    if (!upgradeId) return;
    window.history.replaceState({}, '', '/dashboard');
    const target = sessions.find(s => s.id === upgradeId);
    if (target) void handleStartUpgrade(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions]);

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Find the generated eulogy for a session (if any)
  const getEulogyForSession = (sessionId: string): GeneratedEulogy | undefined => {
    return eulogies.find(e => e.session_id === sessionId);
  };

  // Count contributions for a session
  const getContributionsForSession = (sessionId: string): Contribution[] => {
    return contributions.filter(c => c.session_id === sessionId);
  };

  // Navigation helpers
  const goToConversation = (session: ConversationSession) => {
    navigate(`/conversation?sessionId=${session.id}&type=${session.session_type}&name=${encodeURIComponent(session.deceased_name || '')}`);
  };

  const goToEditor = (eulogyId: string, type?: string) => {
    navigate(`/eulogy/${eulogyId}${type ? `?type=${type}` : ''}`);
  };

  // 2026-07-05: begin writing on a paid-but-unstarted license. When the piece
  // type is known (post-migration-028 licenses) go straight into the
  // conversation flow — the manager creates the conversation from the
  // onboarding details saved on the license (merged with any localStorage
  // copy). Older licenses route through /new-project so the user picks
  // eulogy vs tribute there.
  const beginUnstarted = (lic: UnstartedLicense) => {
    if (lic.piece_type && lic.deceased_name) {
      const params = new URLSearchParams({
        type: lic.piece_type,
        name: lic.deceased_name,
        autoAdvance: 'true',
      });
      // F1: only the owner's relationship rides along. Invited writers give
      // their OWN relationship on the pre-chat form (the server withholds the
      // owner's relationship for them, so this is naturally absent).
      if (lic.relationship) params.set('relationship', lic.relationship);
      navigate(`/conversation?${params.toString()}`);
    } else {
      navigate('/new-project');
    }
  };

  // 2026-07-12: the separate Collaboration Dashboard entry point was retired
  // for the simplified family-memories flow; the /collaboration/:id route
  // still exists for direct links.

  const goToInvite = (session: ConversationSession) => {
    // Phase 6c: Family & Friends owners manage WRITERS (auth-gated React page),
    // Single Tribute owners share the MEMORY ROOM link (static HTML). We pass
    // tier + licenseId so invite-collaborators.html can redirect family_memorial
    // owners to /manage-writers/:licenseId before rendering the memory-room UI.
    const params = new URLSearchParams({
      eulogy_id: session.id,
      name: session.deceased_name || '',
      type: session.session_type,
    });
    if (session.testament_license_id) {
      params.set('licenseId', session.testament_license_id);
    }
    if (session.tier) {
      params.set('tier', session.tier);
    }
    // Pass inviter name so invite emails read "Angelique invited you..." instead
    // of falling back to the literal "Someone" placeholder. Static HTML page
    // can't authenticate to Clerk on its own, so we pass it via URL param.
    const inviterDisplayName = [user?.firstName, user?.lastName].filter(Boolean).join(' ');
    if (inviterDisplayName) {
      params.set('userName', inviterDisplayName);
    }
    window.location.href = `/invite-collaborators.html?${params.toString()}`;
  };

  // Phase 5: download the Memory Collection PDF for a finalised piece. Uses the
  // same blob-download pattern as CyranoEditor's handleDownloadMemoryCollection
  // so errors surface in a small dismissable banner rather than via alert().
  const handleDownloadMemoryCollection = async (session: ConversationSession) => {
    if (!session.testament_license_id) {
      setMcError('Missing license — please open this piece in CyranoEditor.');
      return;
    }
    setMcDownloadingId(session.id);
    setMcError(null);
    try {
      const token = await getToken();
      const resp = await fetch('/api/eulogy/export-memory-collection', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ licenseId: session.testament_license_id }),
      });
      if (!resp.ok) {
        let msg = 'Failed to download Memory Collection.';
        try {
          const j = await resp.json();
          if (j?.error === 'not_finalised') msg = 'This piece is not finalised yet.';
          else if (j?.message) msg = j.message;
        } catch { /* keep default */ }
        setMcError(msg);
        return;
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safe = (session.deceased_name || 'Memory_Collection').replace(/[^a-zA-Z0-9_-]+/g, '_');
      a.download = `${safe}_Memory_Collection.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Memory Collection download error:', err);
      setMcError('Network error — please try again.');
    } finally {
      setMcDownloadingId(null);
    }
  };

  // Phase 6: kick off Family & Friends upgrade checkout. Mirrors the blob-download
  // error surface used by handleDownloadMemoryCollection — a small dismissable
  // banner, not an alert(). The endpoint does full validation (ownership, tier,
  // archived/finalised state) and returns a specific error code on refusal so we
  // can map it to a user-readable message rather than leaking "License not found".
  const handleStartUpgrade = async (session: ConversationSession) => {
    if (!session.testament_license_id) {
      setUpgradeError('Missing license — please open this piece and try again.');
      return;
    }
    setUpgradeStartingId(session.id);
    setUpgradeError(null);
    try {
      const token = await getToken();
      const resp = await fetch('/api/stripe/create-upgrade-checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          licenseId: session.testament_license_id,
          conversationId: session.id,
        }),
      });
      const json = await resp.json().catch(() => ({} as any));
      if (!resp.ok || !json?.success || !json?.checkoutUrl) {
        let msg = 'Could not start the upgrade. Please try again.';
        if (json?.error === 'already_family_memorial') {
          msg = 'This piece is already on the Family & Friends tier.';
        } else if (json?.error === 'license_archived') {
          msg = 'The 90-day writing window has closed. Upgrading is no longer available.';
        } else if (json?.error === 'license_finalised') {
          msg = 'This piece has been finalised. Unfinalise it in CyranoEditor before upgrading.';
        } else if (json?.message) {
          msg = json.message;
        }
        setUpgradeError(msg);
        setUpgradeStartingId(null);
        return;
      }
      // Redirect to Stripe Checkout. We don't clear the loading state here
      // because navigation starts immediately and the button is about to unmount.
      window.location.href = json.checkoutUrl;
    } catch (err) {
      console.error('Upgrade checkout error:', err);
      setUpgradeError('Network error — please try again.');
      setUpgradeStartingId(null);
    }
  };

  if (!isLoaded || loadingData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-warm-background">
        <div className="text-center">
          <img src="/GhostQuillLogo.png" alt="Cyrano" className="w-16 h-16 mx-auto animate-pulse" />
          <p className="text-cool-gray mt-4">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (!isSignedIn) {
    return <Navigate to="/sign-up" replace />;
  }

  const hasProjects = sessions.length > 0 || unstartedLicenses.length > 0;

  // Phase 5: three-way partition — Active (writing open, not finalised),
  // Completed (finalised_at set, not archived), Archived (archived_at set).
  // Archived takes precedence if both flags somehow coexist so an expired
  // piece is never hidden behind the Completed banner.
  const activeSessions = sessions.filter(s => !s.archived_at && !s.finalised_at);
  const completedSessions = sessions.filter(s => !!s.finalised_at && !s.archived_at);
  const archivedSessions = sessions.filter(s => !!s.archived_at);

  // Phase 6: owned vs shared-with-you split for the greeting line. Matches the
  // backward-compat rule in renderSessionCard — missing is_owner means owner.
  const sharedActiveCount = activeSessions.filter(s => s.is_owner === false).length;
  const ownedActiveCount = activeSessions.length - sharedActiveCount;

  const formatArchiveDate = (iso: string | null | undefined): string => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        year: 'numeric', month: 'long', day: 'numeric'
      });
    } catch { return ''; }
  };

  // Card renderer — shared between Active, Completed, and Archived sections.
  // Three distinct states drive behaviour:
  //   Active:    writing open; Continue/Generate + Invite + Collaboration
  //   Completed: finalised; primary CTA = Download Memory Collection, secondary = read in editor
  //   Archived:  90-day window closed; read-only in editor, no Generate/Invite
  // Archived and Completed are mutually exclusive — the partition above ensures
  // archived takes precedence if both flags somehow coexist.
  const renderSessionCard = (
    session: ConversationSession,
    { isArchived, isCompleted: isCompletedFinal }: { isArchived: boolean; isCompleted: boolean }
  ) => {
    const eulogy = getEulogyForSession(session.id);
    const sessionContribs = getContributionsForSession(session.id);
    const isStatusCompleted = session.status === 'completed';
    const isInProgress = !isStatusCompleted && session.status !== 'abandoned';
    const typeLabel = session.session_type === 'eulogy' ? 'Eulogy' : 'Tribute';
    const isDownloadingMc = mcDownloadingId === session.id;
    // Phase 6: default to owner when the field is missing so legacy rows (pre-6
    // /api/user/sessions) render as the user's own work rather than accidentally
    // flashing a "Shared with you" pill during rollout.
    const isOwner = session.is_owner !== false;

    // Visual treatment: completed pieces get a subtle navy left border and a
    // "Finalised" pill in the corner. Archived keeps its pink-lavender stripe.
    const leftBorderColor = isArchived
      ? '#d4adbb'
      : isCompletedFinal
        ? '#19285c'
        : undefined;
    const leftBorderWidth = (isArchived || isCompletedFinal) ? 6 : 1;
    const cardBorderColor = isArchived
      ? 'rgba(212, 173, 187, 0.45)'
      : isCompletedFinal
        ? 'rgba(25, 40, 92, 0.2)'
        : 'rgba(140, 131, 180, 0.15)';

    return (
      <div
        key={session.id}
        className="bg-white rounded-2xl shadow-sm border overflow-hidden"
        style={{
          borderColor: cardBorderColor,
          borderLeftWidth: leftBorderWidth,
          borderLeftColor: leftBorderColor,
        }}
      >
        <div className="p-5 sm:p-6">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="min-w-0">
              <h3
                onClick={() => {
                  if (eulogy) goToEditor(eulogy.id, session.session_type);
                  else if (!isArchived && !isCompletedFinal) goToConversation(session);
                }}
                className="font-serif text-lg sm:text-xl text-space-cadet font-semibold truncate cursor-pointer hover:text-space-cadet/70 transition-colors"
              >
                {typeLabel} for {session.deceased_name || 'Unknown'}
              </h3>
              <p className="text-sm text-cool-gray mt-1">
                {isArchived
                  ? `Archived ${formatArchiveDate(session.archived_at)}`
                  : isCompletedFinal
                    ? `Finalised ${formatArchiveDate(session.finalised_at)}`
                    : `Updated ${formatRelativeTime(session.updated_at)}`}
              </p>
            </div>
            {isArchived ? (
              <span
                className="flex-shrink-0 text-xs font-medium px-3 py-1.5 rounded-full"
                style={{ backgroundColor: 'rgba(212, 173, 187, 0.25)', color: '#19285c' }}
              >
                Archived
              </span>
            ) : isCompletedFinal ? (
              <span
                className="flex-shrink-0 text-xs font-medium px-3 py-1.5 rounded-full"
                style={{ backgroundColor: 'rgba(25, 40, 92, 0.1)', color: '#19285c' }}
              >
                Finalised
              </span>
            ) : !isOwner ? (
              // Phase 6: writers on someone else's license see a distinct pill
              // instead of the progress %. Mutually exclusive with Archived /
              // Finalised / progress pills — state precedence is
              // Archived > Finalised > Shared-with-you > progress%.
              <span
                className="flex-shrink-0 text-xs font-medium px-3 py-1.5 rounded-full"
                style={{ backgroundColor: 'rgba(212, 173, 187, 0.25)', color: '#19285c' }}
                title={session.tier === 'family_memorial' ? 'Family & Friends writer' : 'Read-only viewer'}
              >
                Shared with you
              </span>
            ) : (
              <span className={`flex-shrink-0 text-xs font-medium px-3 py-1.5 rounded-full ${
                isStatusCompleted
                  ? 'bg-green-50 text-green-700'
                  : 'bg-pink-lavender/20 text-space-cadet'
              }`}>
                {isStatusCompleted ? 'Complete' : `${session.completion_percentage}%`}
              </span>
            )}
          </div>

          {/* Progress bar only on active-in-progress cards */}
          {!isArchived && !isCompletedFinal && isInProgress && (
            <div className="w-full h-2 bg-warm-background rounded-full overflow-hidden mb-5">
              <div
                className="h-full bg-space-cadet rounded-full transition-all duration-500"
                style={{ width: `${Math.max(session.completion_percentage, 3)}%` }}
              />
            </div>
          )}

          {/* Read-only explainer on archived cards */}
          {isArchived && (
            <p className="text-sm mb-5 leading-relaxed" style={{ color: '#19285c' }}>
              The 90-day writing window has closed. Your work is preserved — open it
              in CyranoEditor to read, refine the draft, or export it in full.
            </p>
          )}

          {/* Keepsake explainer on completed cards */}
          {isCompletedFinal && (
            <p className="text-sm mb-5 leading-relaxed" style={{ color: '#19285c' }}>
              This piece is finalised. Download your Memory Collection — a keepsake PDF
              with the finalised text, every photo, and every memory your family shared.
            </p>
          )}

          {/* Action buttons. Mobile-first: buttons span the full card width on
              a phone and revert to natural width in an inline wrap row on sm+.
              The shared-memories count lives on the family button's sub-line,
              so no separate summary strip is needed. */}
          <div className="flex flex-wrap gap-3">
            {/* Primary action — differs per state */}
            {isCompletedFinal ? (
              <button
                onClick={() => handleDownloadMemoryCollection(session)}
                disabled={isDownloadingMc || !session.testament_license_id}
                className="w-full sm:w-auto inline-flex items-center justify-center sm:justify-start gap-2 px-5 py-3 min-h-[48px] rounded-xl font-medium transition-colors"
                style={{
                  backgroundColor: '#19285c',
                  color: '#ffffff',
                  opacity: (isDownloadingMc || !session.testament_license_id) ? 0.6 : 1,
                  cursor: isDownloadingMc ? 'wait' : 'pointer',
                }}
              >
                <img
                  src="/Assets/200-handdrawn-icons-by-heloholo/png/48/download48.png"
                  alt=""
                  style={{ width: 18, height: 18, filter: 'brightness(0) invert(1)' }}
                />
                {isDownloadingMc ? 'Preparing…' : 'Download Memory Collection'}
              </button>
            ) : isArchived ? (
              <button
                onClick={() => eulogy ? goToEditor(eulogy.id, session.session_type) : goToEditor(session.id, session.session_type)}
                className="w-full sm:w-auto px-5 py-3 min-h-[48px] rounded-xl font-medium transition-colors"
                style={{ backgroundColor: '#19285c', color: '#ffffff' }}
              >
                Read my work in CyranoEditor
              </button>
            ) : eulogy ? (
              <button
                onClick={() => goToEditor(eulogy.id, session.session_type)}
                className="w-full sm:w-auto px-5 py-3 min-h-[48px] rounded-xl font-medium transition-colors"
                style={{ backgroundColor: '#19285c', color: '#ffffff' }}
              >
                View & Edit {typeLabel}
              </button>
            ) : isStatusCompleted ? (
              <button
                onClick={() => goToConversation(session)}
                className="w-full sm:w-auto px-5 py-3 min-h-[48px] rounded-xl font-medium transition-colors"
                style={{ backgroundColor: '#19285c', color: '#ffffff' }}
              >
                Generate {typeLabel}
              </button>
            ) : (
              <button
                onClick={() => goToConversation(session)}
                className="w-full sm:w-auto px-5 py-3 min-h-[48px] rounded-xl font-medium transition-colors"
                style={{ backgroundColor: '#19285c', color: '#ffffff' }}
              >
                Continue {typeLabel}
              </button>
            )}

            {/* Completed: secondary link into CyranoEditor so user can read/tweak text */}
            {isCompletedFinal && eulogy && (
              <button
                onClick={() => goToEditor(eulogy.id, session.session_type)}
                className="w-full sm:w-auto border border-cool-gray/30 text-space-cadet px-5 py-3 min-h-[48px] rounded-xl font-medium hover:bg-warm-background transition-colors"
              >
                Read &amp; Edit in CyranoEditor
              </button>
            )}

            {/* 2026-07-12 UX simplification: ONE plainly named door for
                everything family & friends — inviting, reading shared
                memories, and (inside that page) the Family & Friends upgrade.
                Replaces the old "Invite Others" + "Collaboration" pair and
                the card-level $40 upgrade button, which a live usability walk
                showed read as jargon and as "inviting costs $40".
                Owner-only: writers on a shared license cannot invite (seat
                cap). The sub-line tells the user what the button does BEFORE
                they tap, or reports progress once memories arrive. */}
            {!isArchived && !isCompletedFinal && isOwner && (
              <button
                onClick={() => goToInvite(session)}
                className="w-full sm:w-auto border px-5 py-3 min-h-[48px] rounded-xl font-medium transition-colors inline-flex items-start justify-center sm:justify-start gap-2 text-left hover:bg-warm-background"
                style={{ borderColor: 'rgba(25, 40, 92, 0.45)', color: '#19285c' }}
              >
                <img
                  src="/Assets/200-handdrawn-icons-by-heloholo/png/48/contacts48.png"
                  alt=""
                  className="w-5 h-5 opacity-70 mt-0.5"
                />
                <span className="flex flex-col items-start">
                  <span>
                    Family &amp; friends&rsquo; memories
                    {sessionContribs.length > 0 ? ` (${sessionContribs.length})` : ''}
                  </span>
                  <span className="text-xs font-normal" style={{ color: '#8c83b4' }}>
                    {sessionContribs.length > 0
                      ? `${sessionContribs.length} memor${sessionContribs.length === 1 ? 'y' : 'ies'} shared so far`
                      : (session.collaborators_invited || 0) > 0
                        ? 'Waiting for the first shared memory'
                        : `Ask others to share their stories of ${(session.deceased_name || 'them').split(' ')[0]}`}
                  </span>
                </span>
              </button>
            )}

            {/* Funeral Guide (paid add-on): plan the service around the piece.
                Owner-only, needs a license; the guide page itself handles the
                locked/unlocked state so this button is always safe to show. */}
            {!isArchived && isOwner && session.testament_license_id && (
              <button
                onClick={() => navigate(`/funeral-guide/${session.testament_license_id}`)}
                className="w-full sm:w-auto border px-5 py-3 min-h-[48px] rounded-xl font-medium transition-colors inline-flex items-start justify-center sm:justify-start gap-2 text-left hover:bg-warm-background"
                style={{ borderColor: 'rgba(25, 40, 92, 0.45)', color: '#19285c' }}
              >
                <img
                  src="/Assets/200-handdrawn-icons-by-heloholo/png/48/list48.png"
                  alt=""
                  className="w-5 h-5 opacity-70 mt-0.5"
                />
                <span className="flex flex-col items-start">
                  <span>Plan the service</span>
                  <span className="text-xs font-normal" style={{ color: '#8c83b4' }}>
                    The full funeral guide, music, and printed booklet
                  </span>
                </span>
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-warm-background flex flex-col">
      <Header />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 flex-1 w-full">

        {/* Empty state — first-time user */}
        {!hasProjects && (
          <div className="bg-white rounded-2xl shadow-sm p-8 sm:p-12 text-center">
            <img src="/GhostQuillLogo.png" alt="" className="w-14 h-14 mx-auto mb-6 opacity-80" />
            <h2 className="font-serif text-2xl mb-3" style={{ color: '#19285c' }}>
              Welcome, {user?.firstName || 'there'}
            </h2>
            <p className="max-w-md mx-auto mb-8 leading-relaxed text-base" style={{ color: '#8c83b4' }}>
              Cyrano will guide you through a conversation to help you create
              a heartfelt eulogy or tribute for your loved one.
            </p>
            <button
              onClick={() => navigate('/onboarding?new=true')}
              className="px-6 sm:px-10 py-4 min-h-[52px] rounded-xl font-semibold text-base sm:text-lg transition-all inline-flex items-center gap-3"
              style={{ backgroundColor: '#19285c', color: '#ffffff', boxShadow: '0 4px 14px rgba(25, 40, 92, 0.3)' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#131e40'
                e.currentTarget.style.transform = 'translateY(-1px)'
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(25, 40, 92, 0.4)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#19285c'
                e.currentTarget.style.transform = 'none'
                e.currentTarget.style.boxShadow = '0 4px 14px rgba(25, 40, 92, 0.3)'
              }}
            >
              <img
                src="/Assets/200-handdrawn-icons-by-heloholo/png/48/plus_circle48.png"
                alt=""
                style={{ width: 24, height: 24, filter: 'brightness(0) invert(1)' }}
              />
              Begin a Eulogy or Tribute
            </button>
          </div>
        )}

        {/* Returning user — project list */}
        {hasProjects && (
          <>
            {/* Top bar: greeting */}
            <div className="mb-8">
              <h2 className="font-serif text-2xl text-space-cadet">
                Welcome back, {user?.firstName || 'there'}
              </h2>
              <p className="text-cool-gray mt-1">
                {sharedActiveCount > 0
                  ? `${ownedActiveCount} active · ${sharedActiveCount} shared with you`
                  : `${activeSessions.length} active`}
                {unstartedLicenses.length > 0 && ` · ${unstartedLicenses.length} ready to begin`}
                {completedSessions.length > 0 && ` · ${completedSessions.length} completed`}
                {archivedSessions.length > 0 && ` · ${archivedSessions.length} archived`}
              </p>
            </div>

            {/* Phase 5: Memory Collection download error banner */}
            {mcError && (
              <div
                className="mb-6 px-4 py-3 rounded-xl flex items-start justify-between gap-3"
                style={{
                  backgroundColor: 'rgba(212, 173, 187, 0.18)',
                  border: '1px solid rgba(212, 173, 187, 0.45)',
                }}
              >
                <p className="text-sm leading-relaxed" style={{ color: '#19285c' }}>
                  {mcError}
                </p>
                <button
                  onClick={() => setMcError(null)}
                  className="text-sm font-medium flex-shrink-0 min-h-[44px] min-w-[44px] px-2"
                  style={{ color: '#19285c' }}
                  aria-label="Dismiss"
                >
                  ×
                </button>
              </div>
            )}

            {/* 2026-07-12: visible progress while the Family & Friends upgrade
                checkout is being prepared (arriving via ?upgrade= from the
                family-memories page) — never leave the user in silence. */}
            {upgradeStartingId && !upgradeError && (
              <div
                className="mb-6 px-4 py-3 rounded-xl flex items-center gap-3"
                style={{
                  backgroundColor: 'rgba(140, 131, 180, 0.12)',
                  border: '1px solid rgba(140, 131, 180, 0.35)',
                }}
              >
                <img
                  src="/Assets/200-handdrawn-icons-by-heloholo/png/48/love48.png"
                  alt=""
                  className="w-5 h-5 opacity-70 animate-pulse"
                />
                <p className="text-sm leading-relaxed" style={{ color: '#19285c' }}>
                  Preparing your Family &amp; Friends upgrade&hellip; taking you to secure payment.
                </p>
              </div>
            )}

            {/* Phase 6: Family & Friends upgrade checkout error banner */}
            {upgradeError && (
              <div
                className="mb-6 px-4 py-3 rounded-xl flex items-start justify-between gap-3"
                style={{
                  backgroundColor: 'rgba(212, 173, 187, 0.18)',
                  border: '1px solid rgba(212, 173, 187, 0.45)',
                }}
              >
                <p className="text-sm leading-relaxed" style={{ color: '#19285c' }}>
                  {upgradeError}
                </p>
                <button
                  onClick={() => setUpgradeError(null)}
                  className="text-sm font-medium flex-shrink-0 min-h-[44px] min-w-[44px] px-2"
                  style={{ color: '#19285c' }}
                  aria-label="Dismiss"
                >
                  ×
                </button>
              </div>
            )}

            {/* 2026-07-05: paid licenses whose conversation hasn't begun yet.
                Payment is when the piece exists — these must never be invisible.
                The onboarding details are saved on the license, so "Begin
                writing" picks up exactly where onboarding left off. */}
            {unstartedLicenses.length > 0 && (
              <section className="mb-10">
                <div className="flex items-baseline justify-between mb-4 px-1">
                  <h3 className="font-serif text-xl text-space-cadet font-semibold">
                    Ready to begin
                  </h3>
                  <span className="text-sm text-cool-gray">
                    {unstartedLicenses.length} {unstartedLicenses.length === 1 ? 'project' : 'projects'}
                  </span>
                </div>
                <div className="space-y-5">
                  {unstartedLicenses.map(lic => (
                    <div
                      key={lic.license_id}
                      className="bg-white rounded-2xl shadow-sm border overflow-hidden"
                      style={{
                        borderColor: 'rgba(25, 40, 92, 0.2)',
                        borderLeftWidth: 6,
                        borderLeftColor: '#8c83b4',
                      }}
                    >
                      <div className="p-5 sm:p-6">
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <div>
                            <h4 className="font-serif text-lg font-semibold" style={{ color: '#19285c' }}>
                              {lic.is_owner === false ? 'Your tribute' : (lic.piece_type === 'tribute' ? 'Tribute' : lic.piece_type === 'eulogy' ? 'Eulogy' : 'Eulogy or Tribute')} for {lic.deceased_name || 'your loved one'}
                            </h4>
                            <p className="text-sm mt-1" style={{ color: '#8c83b4' }}>
                              {lic.is_owner === false
                                ? 'You’ve been invited to write your own piece as part of this shared memorial.'
                                : 'Your details from onboarding are saved — nothing has been lost.'}
                            </p>
                          </div>
                          <span
                            className="text-xs font-medium px-3 py-1 rounded-full flex-shrink-0"
                            style={{ backgroundColor: 'rgba(140, 131, 180, 0.15)', color: '#19285c' }}
                          >
                            {lic.is_owner === false ? 'Shared with you' : 'Ready to begin'}
                          </span>
                        </div>
                        <div className="mt-4">
                          <button
                            onClick={() => beginUnstarted(lic)}
                            className="px-5 py-3 min-h-[44px] rounded-xl font-semibold text-sm transition-all inline-flex items-center gap-2"
                            style={{ backgroundColor: '#19285c', color: '#ffffff' }}
                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#131e40'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#19285c'; }}
                          >
                            <img
                              src="/Assets/200-handdrawn-icons-by-heloholo/png/48/arrow_right48.png"
                              alt=""
                              style={{ width: 20, height: 20, filter: 'brightness(0) invert(1)' }}
                            />
                            {lic.is_owner === false ? 'Begin your tribute' : 'Begin writing'}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Active section — writing window still open */}
            {activeSessions.length > 0 && (
              <section className="mb-10">
                <div className="flex items-baseline justify-between mb-4 px-1">
                  <h3 className="font-serif text-xl text-space-cadet font-semibold">
                    Active
                  </h3>
                  <span className="text-sm text-cool-gray">
                    {activeSessions.length} {activeSessions.length === 1 ? 'project' : 'projects'}
                  </span>
                </div>
                <div className="space-y-5">
                  {activeSessions.map(s => renderSessionCard(s, { isArchived: false, isCompleted: false }))}
                </div>
              </section>
            )}

            {/* Completed section — Phase 5: finalised pieces with Memory Collection download */}
            {completedSessions.length > 0 && (
              <section className="mb-10">
                <div className="flex items-baseline justify-between mb-2 px-1">
                  <h3 className="font-serif text-xl text-space-cadet font-semibold">
                    Completed
                  </h3>
                  <span className="text-sm text-cool-gray">
                    {completedSessions.length} {completedSessions.length === 1 ? 'project' : 'projects'}
                  </span>
                </div>
                <p className="text-sm text-cool-gray mb-4 px-1 leading-relaxed">
                  These pieces are finalised. Download the Memory Collection — a keepsake
                  PDF containing the finalised text, every photo, and every memory shared
                  by family and friends.
                </p>
                <div className="space-y-5">
                  {completedSessions.map(s => renderSessionCard(s, { isArchived: false, isCompleted: true }))}
                </div>
              </section>
            )}

            {/* Archived section — Phase 4: 90-day writing window has closed */}
            {archivedSessions.length > 0 && (
              <section className="mb-6">
                <div className="flex items-baseline justify-between mb-2 px-1">
                  <h3 className="font-serif text-xl text-space-cadet font-semibold">
                    Archived
                  </h3>
                  <span className="text-sm text-cool-gray">
                    {archivedSessions.length} {archivedSessions.length === 1 ? 'project' : 'projects'}
                  </span>
                </div>
                <p className="text-sm text-cool-gray mb-4 px-1 leading-relaxed">
                  The writing window has closed on these pieces. Your work is preserved —
                  open any one in CyranoEditor to read, refine, or export it.
                </p>
                <div className="space-y-5">
                  {archivedSessions.map(s => renderSessionCard(s, { isArchived: true, isCompleted: false }))}
                </div>
              </section>
            )}

            {/* Nudge to start fresh if nothing is active (everything finalised or archived) */}
            {activeSessions.length === 0 && (completedSessions.length > 0 || archivedSessions.length > 0) && (
              <div className="bg-white rounded-2xl shadow-sm p-6 sm:p-8 text-center mt-8">
                <p className="font-serif text-lg mb-4" style={{ color: '#19285c' }}>
                  Ready to begin a new piece?
                </p>
                <button
                  onClick={() => navigate('/new-project')}
                  className="px-6 py-3 min-h-[48px] rounded-xl font-medium transition-colors inline-flex items-center gap-2"
                  style={{ backgroundColor: '#19285c', color: '#ffffff' }}
                >
                  <img
                    src="/Assets/200-handdrawn-icons-by-heloholo/png/48/plus_circle48.png"
                    alt=""
                    style={{ width: 20, height: 20, filter: 'brightness(0) invert(1)' }}
                  />
                  Begin a Eulogy or Tribute
                </button>
              </div>
            )}
          </>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default Dashboard;
